/**
 * The payer's page — `/{slug}/check/{checkToken}` (Guest QR Platform, piece 3,
 * Task 8). A guest scans the QR on their table (or on the printed pre-bill),
 * lands here with a signed check token and nothing else, and pays their check
 * from their phone: the whole balance, an even split, the items they ordered,
 * or an amount they type — with a tip, without signing in.
 *
 * Seven tasks of server sit behind it. This file is the only thing a guest
 * ever sees of any of it.
 *
 * ── OUTSIDE `<CustomerSession>` (Ruling R57) ────────────────────────────────
 * Every other route in this app requires a session token minted by the
 * ordering app; a guest arriving cold from a QR has none, so mounting this
 * page inside that gate blocks it before it renders. `PosKeyedEntryPage` is
 * the precedent. See `App.tsx`, and `checkPayRouting.test.tsx`, which fails if
 * anyone moves it back inside.
 *
 * ── The token is the whole request ─────────────────────────────────────────
 * `checkGuid`, `tenantId` and `restaurantId` are derived from the token
 * server-side and never accepted from a client (spec §4.1). So this page never
 * sends an id, never sees the restaurant document, and learns which card
 * gateway the business uses from the one thing it can ask — the card-session
 * route; a refused session reads as "no card form here", never a guess.
 *
 * ── Four things that are load-bearing ───────────────────────────────────────
 *
 * 1. THE AMOUNT COMES FROM THE SHARED HELPER. `computeGuestCheckBaseCents`
 *    (`@order-up/common`) is the SAME function the charge route calls — moved
 *    there in this task precisely so the figure shown here and the figure
 *    charged cannot drift. The page never reads `order.total` and never
 *    hand-rolls a remainder: that is the plan's Global Constraint, and it
 *    exists because `payment_screen.dart` pre-filled the FULL original total on
 *    a partly-paid check and one tap overcharged a guest. The server still
 *    recomputes after taking the check's pay lock and caps or refuses there —
 *    this is a preview of the same rule, never a promise.
 *
 * 2. THE TIP APPLIES TO THE BASE. A guest paying a quarter of the table's bill
 *    tips on their quarter. `TipSelection` is handed the BASE, not the balance
 *    and not the total.
 *
 * 3. THE BILLING POSTAL IS REQUIRED HERE (R63) even though the wire field is
 *    optional. Without it the sale carries no `avsresp` at all, and
 *    `evaluateAvsCvvRule` only pushes a rejection when `outcome.avs !==
 *    undefined` — so it returns ALLOW. It fails OPEN. A restaurant that
 *    deliberately set `enforcement: 'reject'` would get NO AVS protection on
 *    the most exposed card-not-present surface the platform has, silently. A
 *    fraud control that is inert is worse than one that is absent.
 *
 * 4. THE BALANCE IS POLLED, AND EVERY REFUSAL READS DIFFERENTLY. Other guests
 *    at the same table are paying the same check on their own phones, so the
 *    bill is re-read every 3 seconds while this page is visible (the poll and
 *    its cancellation live in `useGuestCheck`), and the server's refusals are
 *    rendered one by one. `ITEMS_ALREADY_CLAIMED` is NOT a failure: someone
 *    else covered what this guest picked, the check still has money owed on it,
 *    and the honest answer is to offer the remaining balance.
 *
 * ── Apple Pay / Google Pay ──────────────────────────────────────────────────
 * Offered above the card form, because a guest who has already unlocked their
 * phone to scan a QR should not then type a PAN on it. WHICH buttons is not
 * this page's opinion: `resolveWalletOwner` reads the gateway the card-session
 * route named, exactly as `CheckoutPage` does — TRX gets TRX's own buttons
 * mounted inside its hosted fields (TRX supplies the gateway config itself).
 *
 * Three things that bind, each of which has broken a payment surface before:
 *
 *   a. A WALLET BUTTON FAILING TO MOUNT IS NEVER FATAL TO THE CARD FORM. The
 *      TRX arm partitions wallet-only init errors off inside `<TrxCardFields>`
 *      (`partitionTrxInitErrors`); Apple Pay is asked for only where the
 *      browser can present it. Whatever happens to the buttons, the card form
 *      below them stays mounted and Pay stays available.
 *   b. THE CRYPTOGRAM EXPIRES IN ABOUT A MINUTE. The charge is submitted from
 *      the sheet's own callback — there is no second confirm, no Pay press and
 *      nothing that waits on the guest. `submitCharge` is therefore ONE
 *      function both arms enter, so the wallet arm cannot quietly drift out of
 *      step with the card arm on what it sends or how it reads a refusal.
 *   c. THE POSTAL IS SENT ON THE WALLET ARM TOO (R63). A wallet is not exempt:
 *      a live TRX wallet sale without it came back `avsresp: "N"`, which a
 *      restaurant enforcing `reject` would decline outright. The DPAN and
 *      cryptogram authenticate the payer; the postal is what keeps the sale on
 *      the AVS-matched interchange tier and off a false decline. So the field
 *      is asked for ABOVE the buttons, and a sheet completed without one is
 *      refused with something the guest can act on rather than charged
 *      without AVS. That costs one tap in the rare case; it is the only step
 *      on this page that a guest may have to repeat, and it beats a decline
 *      they cannot explain.
 */

import {
    computeGuestCheckBaseCents,
    type GuestCheckPaySelection,
    type GuestCheckView,
    percentageTipCents,
} from '@order-up/common';
import {
    createClientLogger,
    isCheckMiss,
    payGuestCheck,
    startCheckCardSession,
    useGuestCheck,
    type CheckCardSessionOutcome,
    type CheckPaymentInstrument,
    type CheckWalletPayment,
    type GuestCheckPayBody,
    type GuestCheckPayResult,
} from '@order-up/client/vite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import CheckCardEntry, { type CheckCardEntryHandle } from '../components/CheckCardEntry';
import TipSelection from '../components/TipSelection';
import { resolveWalletOwner } from '../lib/walletOwner';

const logger = createClientLogger('CheckPayPage');

/** How the payer is covering the check. `evenShare` is always the FIRST part — see below. */
type PayMode = GuestCheckPaySelection['mode'];

/**
 * Every guest takes part 1 of N, never "the last part".
 *
 * The base helper gives the LAST part the rounding cent so the parts sum to
 * the balance exactly. A phone has no way to know which payer it is, so it
 * always asks for the floor share; the guest who pays last finds the true
 * remainder sitting in the balance and takes it with "the whole balance". No
 * cent is invented, and none is lost.
 */
const EVEN_SHARE = 1;

const MIN_EVEN_PARTS = 2;
const MAX_EVEN_PARTS = 10;

/** Matches the server's `billingZip` bounds exactly — 3–10 of letters/digits/space/hyphen. */
const POSTAL_PATTERN = /^[A-Za-z0-9 -]{3,10}$/;

function money(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
}

/**
 * How each refusal the charge route can answer with is rendered.
 *
 * `tone: 'info'` is NOT cosmetic. `ITEMS_ALREADY_CLAIMED` and `NOTHING_DUE`
 * mean the money is fine and something changed at the table; showing either as
 * a red failure tells a guest their payment broke when it did not. `closes`
 * takes the form away entirely, for the two outcomes where pressing Pay again
 * is the wrong thing to do.
 */
interface RefusalRule {
    readonly tone: 'info' | 'error';
    readonly title: string;
    /** Shown when the server sent no message of its own. */
    readonly fallback: string;
    /** Re-read the bill: the figure this page is showing is out of date. */
    readonly refetch?: boolean;
    /** Take the payment form away — retrying is the wrong move. */
    readonly closes?: boolean;
    /** Offer "pay the remaining balance" — the selection lost a race. */
    readonly offersFull?: boolean;
    /** Drop the item selection: it names something the check no longer has. */
    readonly clearsItems?: boolean;
    /** Switch the card fields back on so another card can be tried. */
    readonly reEnablesCard?: boolean;
}

const REFUSALS: Record<string, RefusalRule> = {
    ITEMS_ALREADY_CLAIMED: {
        tone: 'info',
        title: 'Someone already got those',
        fallback:
            'Someone already covered everything you selected. Pick something else, or pay the remaining balance.',
        refetch: true,
        offersFull: true,
    },
    NOTHING_DUE: {
        tone: 'info',
        title: 'This check is settled',
        fallback: 'This check has nothing left to pay.',
        refetch: true,
    },
    PAYMENT_IN_PROGRESS: {
        tone: 'info',
        title: 'Someone else is paying right now',
        fallback:
            'Someone at your table is paying this check right now. Please try again in a moment.',
    },
    AMOUNT_EXCEEDS_BALANCE: {
        tone: 'error',
        title: 'That is more than is left',
        fallback:
            'Someone may have already paid part of this check. We have refreshed the balance — please try again.',
        refetch: true,
    },
    UNKNOWN_ITEM: {
        tone: 'error',
        title: 'That item is no longer on this check',
        fallback: 'We have refreshed the bill — please choose again.',
        refetch: true,
        clearsItems: true,
    },
    CHECK_NOT_PAYABLE: {
        tone: 'error',
        title: 'This check is closed',
        fallback: 'This check is no longer accepting payment. Please ask your server.',
        closes: true,
    },
    PAYMENT_NEEDS_RECONCILIATION: {
        tone: 'error',
        title: 'Please ask your server',
        fallback:
            'We could not confirm whether an earlier payment on this check went through. Please ask your server — do not pay again.',
        closes: true,
    },
    CARD_DECLINED: {
        tone: 'error',
        title: 'Your card was declined',
        fallback: 'Your bank did not approve this payment. You can try another card.',
        reEnablesCard: true,
    },
    CARD_REJECTED: {
        tone: 'error',
        title: 'We could not process this payment',
        fallback:
            'This payment could not be processed. Please ask your server — trying another card will not help.',
    },
    CARD_PROVIDER_MISMATCH: {
        tone: 'error',
        title: 'Please reload this page',
        fallback:
            'Your card was entered for a different payment gateway than this business uses. Reload the page and enter it again.',
    },
    MERCHANT_NOT_CONFIGURED: {
        tone: 'error',
        title: 'This business cannot take a card here',
        fallback: 'This business is not set up to take a payment from your phone.',
        closes: true,
    },
};

const GENERIC_REFUSAL: RefusalRule = {
    tone: 'error',
    title: 'We could not take this payment',
    fallback: 'Please try again in a moment.',
    reEnablesCard: true,
};

interface Refusal {
    readonly code: string;
    readonly rule: RefusalRule;
    readonly message: string;
}

export default function CheckPayPage() {
    const { checkToken = '' } = useParams<{ slug: string; checkToken: string }>();

    // The bill, re-read every 3s while this page is visible. The poll (and its
    // cancellation on unmount) lives in the hook.
    const { data: check, isLoading, error, refetch } = useGuestCheck(checkToken);

    const [mode, setMode] = useState<PayMode>('full');
    const [selectedItems, setSelectedItems] = useState<readonly string[]>([]);
    const [evenParts, setEvenParts] = useState(MIN_EVEN_PARTS);
    const [amountInput, setAmountInput] = useState('');
    const [tipPercent, setTipPercent] = useState(20);
    const [customTip, setCustomTip] = useState('');
    const [billingZip, setBillingZip] = useState('');

    const [gateway, setGateway] = useState<CheckCardSessionOutcome | null>(null);
    const [cardReady, setCardReady] = useState(false);
    const [cardNotice, setCardNotice] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [refusal, setRefusal] = useState<Refusal | null>(null);
    const [receipt, setReceipt] = useState<GuestCheckPayResult | null>(null);
    // Bumped to mint a FRESH card session — a PayPage session and its field
    // iframes are spent once a charge has gone through them.
    const [sessionAttempt, setSessionAttempt] = useState(0);

    const cardRef = useRef<CheckCardEntryHandle>(null);

    const linkDead = isCheckMiss(error);

    // ── The card session: minted ONCE per visit ─────────────────────────────
    // The bill polls every three seconds; the session mint is on the SPEND
    // tier (6/min) and must never ride that poll. Keyed on the token and the
    // explicit "pay another part" attempt, nothing else.
    useEffect(() => {
        if (!checkToken || linkDead) return;
        let cancelled = false;
        startCheckCardSession(checkToken)
            .then((outcome) => {
                if (!cancelled) setGateway(outcome);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                // A miss is the PAGE's state (handled below by `linkDead`);
                // anything else leaves the bill readable and the card form
                // absent, which is the honest thing to show.
                logger.warn('check card session failed', {
                    error: err instanceof Error ? err.message : String(err),
                });
                setGateway({
                    kind: 'unavailable',
                    code: 'CARD_SESSION_UNAVAILABLE',
                    message:
                        'The secure card form could not be started. Please reload the page, or ask your server.',
                });
            });
        return () => {
            cancelled = true;
        };
    }, [checkToken, linkDead, sessionAttempt]);

    // ── What this payer is covering ─────────────────────────────────────────
    const amountCents = useMemo(() => {
        const parsed = Number.parseFloat(amountInput);
        return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : undefined;
    }, [amountInput]);

    const selection = useMemo<GuestCheckPaySelection>(() => {
        switch (mode) {
            case 'items':
                return { mode, itemGuids: selectedItems };
            case 'even':
                return { mode, evenParts, evenShare: EVEN_SHARE };
            case 'amount':
                return { mode, ...(amountCents !== undefined ? { amountCents } : {}) };
            default:
                return { mode: 'full' };
        }
    }, [mode, selectedItems, evenParts, amountCents]);

    // The SAME function the charge route calls (see the file header). The page
    // passes no claim set — it is not told which items other payers covered —
    // so its preview can only over-estimate, and the server trims it.
    const baseOutcome = useMemo(
        () => (check ? computeGuestCheckBaseCents(check, selection) : null),
        [check, selection],
    );
    const baseCents = baseOutcome?.ok ? baseOutcome.baseCents : 0;

    // ONE derivation for the tiles and the charge (`percentageTipCents`, the
    // till's half-away-from-zero rule; no tip on a non-positive base or preset).
    const tipCentsFor = useCallback(
        (percentage: number) =>
            Number.isFinite(percentage) && percentage > 0 ? percentageTipCents(baseCents, percentage) : 0,
        [baseCents],
    );

    const tipCents = useMemo(() => {
        if (customTip) {
            const parsed = Number.parseFloat(customTip);
            return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : 0;
        }
        // Derived from the BASE, in minor units, so the percentage the guest
        // picked lands on what THEY are paying.
        // The till's rule, shared: the SAME `tipCentsFor` the tip tiles show.
        return tipCentsFor(tipPercent);
    }, [customTip, tipPercent, tipCentsFor]);

    const totalCents = baseCents + tipCents;
    const postalValid = POSTAL_PATTERN.test(billingZip.trim());
    const closed = refusal?.rule.closes === true;

    /**
     * Everything the charge needs that is NOT an instrument. The card path
     * adds `cardReady` on top; a wallet sheet carries its own instrument and
     * has nothing to do with whether the hosted fields finished mounting — so
     * gating the buttons on `cardReady` would hide them for a guest whose card
     * form is still loading, which is exactly the guest a wallet is for.
     */
    const canCharge =
        !!check &&
        !check.fullyPaid &&
        !closed &&
        !receipt &&
        gateway?.kind !== 'unavailable' &&
        !!gateway &&
        baseOutcome?.ok === true &&
        postalValid &&
        !submitting;
    const canPay = canCharge && cardReady;

    /**
     * Which wallet buttons this business's gateway gives us. `'none'` for an
     * unavailable card session, which is correct — there is nothing to pay
     * with at all in that state.
     */
    const walletOwner = resolveWalletOwner(gateway?.kind);

    // ── The charge ──────────────────────────────────────────────────────────
    /**
     * ONE submit path, two instruments. The card arm extracts a blob at the
     * press; the wallet arm arrives holding one already. Everything after that
     * — the body, the refusal rendering, the receipt — is identical, and
     * keeping it identical is the point: a second copy is where one arm
     * quietly stops sending the postal, or stops re-reading the bill after
     * `ITEMS_ALREADY_CLAIMED`.
     */
    const submitCharge = useCallback(
        async (
            instrument:
                | { readonly payment: CheckPaymentInstrument }
                | { readonly walletPayment: CheckWalletPayment },
        ) => {
            const body: GuestCheckPayBody = {
                ...selection,
                tipCents,
                // R63 — on BOTH arms. See the file header: a wallet sale sent
                // without it comes back `avsresp: "N"`.
                billingZip: billingZip.trim(),
                ...instrument,
            };
            try {
                const result = await payGuestCheck(checkToken, body);
                setReceipt(result);
                setCardReady(false);
                void refetch();
            } catch (err) {
                const code = (err as { code?: string } | undefined)?.code ?? 'CHECK_PAY_ERROR';
                const rule = REFUSALS[code] ?? GENERIC_REFUSAL;
                const message =
                    err instanceof Error && err.message && !/^Request failed/i.test(err.message)
                        ? err.message
                        : rule.fallback;
                logger.warn('check payment refused', { code });
                setRefusal({ code, rule, message });
                if (rule.clearsItems) setSelectedItems([]);
                if (rule.refetch) void refetch();
                if (rule.reEnablesCard) cardRef.current?.enableFields();
            }
        },
        [selection, tipCents, billingZip, checkToken, refetch],
    );

    const handlePay = useCallback(async () => {
        if (!check || !baseOutcome?.ok || !cardRef.current) return;
        setSubmitting(true);
        setRefusal(null);
        try {
            // Extracted HERE and nowhere earlier: the encrypted blob carries a
            // TTL and the extract deactivates the fields.
            const payment = await cardRef.current.extract();
            await submitCharge({ payment });
        } catch (err) {
            // Only an EXTRACT failure reaches here — `submitCharge` renders
            // its own refusals. The message is the card form's own
            // ("finish entering your card details"), which is the one thing
            // the guest can act on.
            const rule = GENERIC_REFUSAL;
            const message = err instanceof Error && err.message ? err.message : rule.fallback;
            setRefusal({ code: 'CARD_FIELDS_REJECTED', rule, message });
            cardRef.current?.enableFields();
        } finally {
            setSubmitting(false);
        }
    }, [check, baseOutcome, submitCharge]);

    /**
     * A completed Apple/Google Pay sheet. Submitted HERE, synchronously with
     * the callback — the cryptogram is good for about a minute and there is no
     * re-issuing it, so there is no confirmation step, no Pay press, and
     * nothing that waits on the guest.
     *
     * The postal check is not redundant with the inert-until-valid gate on the
     * buttons: a guest can clear the field while the sheet is open. It refuses
     * rather than charging without AVS on a card-not-present sale.
     */
    const handleWalletResult = useCallback(
        (walletPayment: CheckWalletPayment) => {
            if (!check || !baseOutcome?.ok || submitting) return;
            if (!POSTAL_PATTERN.test(billingZip.trim())) {
                setRefusal({
                    code: 'BILLING_POSTAL_REQUIRED',
                    rule: GENERIC_REFUSAL,
                    message:
                        'Please enter your ZIP or postal code, then tap the wallet button again.',
                });
                return;
            }
            setSubmitting(true);
            setRefusal(null);
            void submitCharge({ walletPayment }).finally(() => setSubmitting(false));
        },
        [check, baseOutcome, submitting, billingZip, submitCharge],
    );

    const payRemainingBalance = useCallback(() => {
        setMode('full');
        setSelectedItems([]);
        setRefusal(null);
    }, []);

    const payAnotherPart = useCallback(() => {
        setReceipt(null);
        setRefusal(null);
        setCardNotice(null);
        setSessionAttempt((n) => n + 1);
        setGateway(null);
    }, []);

    const toggleItem = useCallback((orderItemGuid: string) => {
        setSelectedItems((current) =>
            current.includes(orderItemGuid)
                ? current.filter((guid) => guid !== orderItemGuid)
                : [...current, orderItemGuid],
        );
    }, []);

    // ── Render ──────────────────────────────────────────────────────────────

    if (linkDead) {
        return (
            <Shell>
                <div
                    data-testid="link-expired"
                    className="rounded-2xl border border-gray-200 bg-white p-6 text-center"
                >
                    <h1 className="mb-2 text-xl font-semibold text-gray-900">
                        This payment link is no longer live
                    </h1>
                    <p className="text-sm text-gray-600">
                        Scan the QR on your table again, or ask a member of staff.
                    </p>
                </div>
            </Shell>
        );
    }

    if (!check) {
        return (
            <Shell>
                <div
                    data-testid={isLoading ? 'check-loading' : 'check-unavailable'}
                    className="rounded-2xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-600"
                >
                    {isLoading
                        ? 'Loading your check…'
                        : 'We could not load your check just now. Please try again in a moment.'}
                </div>
            </Shell>
        );
    }

    return (
        <Shell>
            <h1 className="mb-4 text-2xl font-bold text-gray-900">Pay your check</h1>

            <Bill check={check} />

            {/* The receipt outlives the form AND the fully-paid banner: the
                poll can flip this check to settled a second after the charge
                lands, and a guest who just paid must not watch their reference
                number disappear. */}
            {receipt && (
                <div
                    data-testid="payment-receipt"
                    className="mt-4 rounded-2xl border border-green-200 bg-green-50 p-5"
                >
                    <p className="text-base font-semibold text-green-900">
                        Paid {money(receipt.chargedCents)}
                    </p>
                    <p className="mt-1 text-sm text-green-800">
                        {receipt.cardLast4 ? `Card ending ${receipt.cardLast4} · ` : ''}
                        Reference {receipt.processorReference}
                    </p>
                    {check.balanceCents > 0 && (
                        <button
                            type="button"
                            data-testid="pay-another-part"
                            onClick={payAnotherPart}
                            className="mt-3 rounded-lg border border-green-300 px-3 py-1.5 text-sm font-semibold text-green-900 pointer-coarse:min-h-11"
                        >
                            Pay another part ({money(check.balanceCents)} left)
                        </button>
                    )}
                </div>
            )}

            {check.fullyPaid ? (
                <div
                    data-testid="fully-paid"
                    className="mt-4 rounded-2xl border border-green-200 bg-green-50 p-5 text-center"
                >
                    <p className="text-base font-semibold text-green-900">This check is fully paid</p>
                    <p className="mt-1 text-sm text-green-800">Thank you — you are all set.</p>
                </div>
            ) : (
                <>
                    {refusal && (
                        <div
                            data-testid="refusal"
                            data-code={refusal.code}
                            data-tone={refusal.rule.tone}
                            role="alert"
                            className={`mt-4 rounded-2xl border p-4 ${
                                refusal.rule.tone === 'info'
                                    ? 'border-blue-200 bg-blue-50'
                                    : 'border-red-200 bg-red-50'
                            }`}
                        >
                            <p
                                className={`text-sm font-semibold ${
                                    refusal.rule.tone === 'info' ? 'text-blue-900' : 'text-red-900'
                                }`}
                            >
                                {refusal.rule.title}
                            </p>
                            <p
                                className={`mt-1 text-sm ${
                                    refusal.rule.tone === 'info' ? 'text-blue-800' : 'text-red-800'
                                }`}
                            >
                                {refusal.message}
                            </p>
                            {refusal.rule.offersFull && (
                                <button
                                    type="button"
                                    data-testid="switch-to-full"
                                    onClick={payRemainingBalance}
                                    className="mt-3 rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white pointer-coarse:min-h-11"
                                >
                                    Pay the remaining balance
                                </button>
                            )}
                        </div>
                    )}

                    {!closed && !receipt && (
                        <>
                            <section className="mt-6" aria-label="What are you paying?">
                                <h2 className="mb-3 text-lg font-semibold text-gray-900">
                                    What are you paying?
                                </h2>
                                <div role="radiogroup" className="space-y-2">
                                    <ModeOption
                                        checked={mode === 'full'}
                                        label="The whole balance"
                                        onSelect={() => setMode('full')}
                                    />
                                    <ModeOption
                                        checked={mode === 'items'}
                                        label="Pick items"
                                        onSelect={() => setMode('items')}
                                    />
                                    <ModeOption
                                        checked={mode === 'even'}
                                        label="Split evenly"
                                        onSelect={() => setMode('even')}
                                    />
                                    <ModeOption
                                        checked={mode === 'amount'}
                                        label="Enter an amount"
                                        onSelect={() => setMode('amount')}
                                    />
                                </div>

                                {mode === 'items' && (
                                    <ul className="mt-3 space-y-2" data-testid="item-picker">
                                        {check.lines.map((line) => (
                                            <li key={line.orderItemGuid}>
                                                <label className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedItems.includes(line.orderItemGuid)}
                                                        onChange={() => toggleItem(line.orderItemGuid)}
                                                    />
                                                    <span className="flex-1">
                                                        {line.quantity > 1 ? `${line.quantity}× ` : ''}
                                                        {line.name}
                                                    </span>
                                                    <span>{money(line.lineTotalCents)}</span>
                                                </label>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {mode === 'even' && (
                                    <div className="mt-3">
                                        <label
                                            htmlFor="even-parts"
                                            className="mb-1 block text-sm font-medium text-gray-700"
                                        >
                                            Split how many ways?
                                        </label>
                                        <select
                                            id="even-parts"
                                            value={evenParts}
                                            onChange={(e) => setEvenParts(Number(e.target.value))}
                                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                        >
                                            {Array.from(
                                                { length: MAX_EVEN_PARTS - MIN_EVEN_PARTS + 1 },
                                                (_, i) => i + MIN_EVEN_PARTS,
                                            ).map((parts) => (
                                                <option key={parts} value={parts}>
                                                    {parts} ways
                                                </option>
                                            ))}
                                        </select>
                                        <p className="mt-1 text-xs text-gray-500">
                                            Everyone pays an equal share; whoever pays last covers the
                                            rounding.
                                        </p>
                                    </div>
                                )}

                                {mode === 'amount' && (
                                    <div className="mt-3">
                                        <label
                                            htmlFor="pay-amount"
                                            className="mb-1 block text-sm font-medium text-gray-700"
                                        >
                                            Amount to pay
                                        </label>
                                        <input
                                            id="pay-amount"
                                            type="text"
                                            inputMode="decimal"
                                            value={amountInput}
                                            onChange={(e) => setAmountInput(e.target.value)}
                                            placeholder="0.00"
                                            className="w-36 rounded-lg border border-gray-300 px-3 py-2 text-sm pointer-coarse:min-h-11 pointer-coarse:text-base"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            We will never charge more than the {money(check.balanceCents)}{' '}
                                            left on this check.
                                        </p>
                                    </div>
                                )}
                            </section>

                            <section className="mt-6" aria-label="Tip">
                                {/* The BASE, not the balance and not the check total. */}
                                <TipSelection
                                    subtotal={baseCents / 100}
                                    tipCentsFor={tipCentsFor}
                                    selectedTip={tipPercent}
                                    customTip={customTip}
                                    onTipChange={(percentage) => {
                                        setTipPercent(percentage);
                                        setCustomTip('');
                                    }}
                                    onCustomTipChange={(amountInDollars) => {
                                        setTipPercent(0);
                                        setCustomTip(String(amountInDollars));
                                    }}
                                />
                            </section>

                            <section className="mt-2 rounded-2xl border border-gray-200 bg-white p-4">
                                <Row label="Your share" value={money(baseCents)} testId="pay-base" />
                                <Row label="Tip" value={money(tipCents)} testId="pay-tip" />
                                <Row
                                    label="You pay"
                                    value={money(totalCents)}
                                    testId="pay-total"
                                    strong
                                />
                            </section>

                            <section className="mt-6" aria-label="How you are paying">
                                {/* ABOVE the wallet buttons deliberately: it is
                                    needed by both arms (R63), and a sheet
                                    completed without it has to be refused. */}
                                <label
                                    htmlFor="billing-zip"
                                    className="mb-1 block text-sm font-medium text-gray-700"
                                >
                                    ZIP or postal code
                                </label>
                                <input
                                    id="billing-zip"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="postal-code"
                                    value={billingZip}
                                    onChange={(e) => setBillingZip(e.target.value)}
                                    aria-describedby="billing-zip-why"
                                    className="w-36 rounded-lg border border-gray-300 px-3 py-2 text-sm pointer-coarse:min-h-11 pointer-coarse:text-base"
                                />
                                <p id="billing-zip-why" className="mb-4 mt-1 text-xs text-gray-500">
                                    Your card&apos;s billing ZIP — needed for a card or a wallet.
                                </p>

                                {gateway && gateway.kind !== 'unavailable' ? (
                                    <CheckCardEntry
                                        ref={cardRef}
                                        gateway={gateway}
                                        onReady={setCardReady}
                                        onValidationError={setCardNotice}
                                        enableWallets={walletOwner === 'trx-library'}
                                        onWalletResult={handleWalletResult}
                                    />
                                ) : gateway ? (
                                    <p
                                        data-testid="card-unavailable"
                                        className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
                                    >
                                        {gateway.message}
                                    </p>
                                ) : (
                                    <p className="text-sm text-gray-500">Loading the secure card form…</p>
                                )}

                                {/* An init error is SHOWN and is never fatal: the card form
                                    stays mounted and Pay stays available. */}
                                {cardNotice && (
                                    <p
                                        data-testid="card-notice"
                                        className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                                    >
                                        {cardNotice}
                                    </p>
                                )}
                            </section>

                            {/* Card-hold disclosure — shown only when the server
                                will AUTHORIZE this card and capture it at end of
                                day (card-session `cardHold`, the same decision
                                `/pay` acts on). Mirrors online checkout's copy;
                                a pay-at-table share is captured exactly as
                                authorized, so there is no "changes to your
                                order" clause. */}
                            {gateway?.kind === 'trx' && gateway.cardHold && (
                                <p
                                    data-testid="card-hold-notice"
                                    className="mt-4 text-xs leading-relaxed text-gray-500"
                                >
                                    Your card will be authorized for this amount now and charged when
                                    the restaurant closes out for the day.
                                </p>
                            )}

                            {gateway?.kind !== 'unavailable' && (
                                <button
                                    type="button"
                                    data-testid="pay-button"
                                    disabled={!canPay}
                                    onClick={() => void handlePay()}
                                    className="mt-6 w-full rounded-xl bg-gray-900 py-4 text-base font-semibold text-white disabled:opacity-50"
                                >
                                    {submitting ? (
                                        <span className="motion-safe:animate-pulse">Paying…</span>
                                    ) : (
                                        `Pay ${money(totalCents)}`
                                    )}
                                </button>
                            )}

                            {!baseOutcome?.ok && baseOutcome && (
                                <p data-testid="base-hint" className="mt-2 text-center text-sm text-gray-600">
                                    {baseOutcome.message}
                                </p>
                            )}
                        </>
                    )}
                </>
            )}

            <p className="mt-8 text-center text-xs text-gray-500">Powered by OrderUp</p>
        </Shell>
    );
}

// ─────────────────────────── presentational pieces ─────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
    return (
        <main className="min-h-svh bg-gray-50 px-4 py-6">
            <div className="mx-auto max-w-lg">{children}</div>
        </main>
    );
}

function Row({
    label,
    value,
    testId,
    strong,
}: {
    label: string;
    value: string;
    testId?: string;
    strong?: boolean;
}) {
    return (
        <div className="flex items-center justify-between py-1 text-sm">
            <span className={strong ? 'font-semibold text-gray-900' : 'text-gray-600'}>{label}</span>
            <span
                {...(testId ? { 'data-testid': testId } : {})}
                className={strong ? 'font-semibold text-gray-900' : 'text-gray-900'}
            >
                {value}
            </span>
        </div>
    );
}

function ModeOption({
    checked,
    label,
    onSelect,
}: {
    checked: boolean;
    label: string;
    onSelect: () => void;
}) {
    return (
        <label
            className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-sm font-medium ${
                checked ? 'border-gray-900 bg-white text-gray-900' : 'border-gray-200 bg-white text-gray-700'
            }`}
        >
            <input type="radio" name="pay-mode" checked={checked} onChange={onSelect} />
            {label}
        </label>
    );
}

/** The bill itself — what was ordered, what it came to, and what is already paid. */
function Bill({ check }: { check: GuestCheckView }) {
    return (
        <section
            aria-label="Your bill"
            className="rounded-2xl border border-gray-200 bg-white p-4"
        >
            <ul data-testid="check-lines" className="mb-3 space-y-2">
                {check.lines.map((line) => (
                    <li key={line.orderItemGuid} className="flex items-start justify-between text-sm">
                        <span className="pr-3 text-gray-900">
                            <span>
                                {line.quantity > 1 ? `${line.quantity}× ` : ''}
                                {line.name}
                            </span>
                            {line.modifiers.length > 0 && (
                                <span className="block text-xs text-gray-500">
                                    {line.modifiers.join(', ')}
                                </span>
                            )}
                        </span>
                        <span className="text-gray-900">{money(line.lineTotalCents)}</span>
                    </li>
                ))}
            </ul>

            <div className="border-t border-gray-100 pt-2">
                <Row label="Subtotal" value={money(check.subtotalCents)} />
                {check.discountCents > 0 && (
                    <Row label="Discount" value={`−${money(check.discountCents)}`} />
                )}
                {check.serviceChargeCents > 0 && (
                    <Row label="Service" value={money(check.serviceChargeCents)} />
                )}
                <Row label="Tax" value={money(check.taxCents)} />
                <Row label="Total" value={money(check.totalCents)} testId="check-total" strong />
                {check.paidCents > 0 && (
                    <Row label="Already paid" value={money(check.paidCents)} testId="already-paid" />
                )}
                <Row
                    label="Balance due"
                    value={money(check.balanceCents)}
                    testId="balance-due"
                    strong
                />
            </div>

            {check.tenderSummaries.length > 0 && (
                <ul
                    data-testid="tender-summaries"
                    className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-600"
                >
                    {check.tenderSummaries.map((tender, index) => (
                        <li
                            key={`${tender.methodLabel}-${tender.last4 ?? 'none'}-${index}`}
                            className="flex items-center justify-between py-0.5"
                        >
                            <span>
                                {tender.methodLabel}
                                {tender.last4 ? ` ••••${tender.last4}` : ''}
                            </span>
                            <span>{money(tender.amountCents)}</span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
